import { useCallback, useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import {applyBlockElements, debounce, getUsercentricsServices, setIframeSrc, waitForApi} from "./consentUtils";

interface ConsentStatus {
    [serviceId: string]: boolean | undefined;
}

const useOverlayService = () => {
    const [services, setServices] = useState<{ name: string; selector: string; serviceId: string }[]>([]);
    const overlayStateRef = useRef<Record<string, boolean>>({});
    const iframeStateRef = useRef<Record<string, boolean>>({});
    const [isReady, setIsReady] = useState(false);
    const [pendingInitialConsent, setPendingInitialConsent] = useState<ConsentStatus | null>(null);
    const pathname = usePathname();

    const fetchServices = async () => {
        await waitForApi();

        const rawServices = getUsercentricsServices();

        const detectedServices = Object.entries(rawServices).map(([serviceId, service]: [string, any]) => {
            return {
                name: service.name,
                selector: `iframe[data-service='${service.name}']`,
                serviceId: serviceId,
            };
        });

        setServices(detectedServices);
        setIsReady(true);
    };

    const blockOverlayForService = useCallback(
        (service: { name: string; selector: string; serviceId: string }) => {
            applyBlockElements(service);
        },
        []
    );

    const unblockIframe = useCallback((iframe: HTMLIFrameElement, serviceName: string) => {
        setIframeSrc(iframe, "Unblock iframe");
        iframeStateRef.current[serviceName] = true;
    }, []);

    const handleOverlayService = useCallback(
        (serviceName: string, consentStatus: ConsentStatus) => {
            const service = services.find((s) => s.name === serviceName);
            if (!service) {
                return;
            }
            const rawService = getUsercentricsServices()[service.serviceId];
            const hasConsentInRawService = rawService?.consent?.given === true;

            // Check if consent exists using service name in the consent status
            const consentByName = consentStatus[service.name];

            const currentConsent = hasConsentInRawService || consentByName;

            const iframes = document.querySelectorAll(service.selector);

            if (iframes.length === 0 && !overlayStateRef.current[service.name]) {
                return;
            }

            if (!currentConsent) {
                blockOverlayForService(service);
            } else {
                iframes.forEach((iframe) => unblockIframe(iframe as HTMLIFrameElement, serviceName));
                overlayStateRef.current[service.name] = false;
            }
        },
        [services, blockOverlayForService, unblockIframe]
    );

    const handleConsentStatusChange = useCallback(
        (consentStatus: ConsentStatus) => {
            services.forEach(({ name }) => {
                handleOverlayService(name, consentStatus);
            });
        },
        [services, handleOverlayService]
    );

    const debouncedResizeHandler = debounce(() => {
        services.forEach((service) => {
            const iframes = document.querySelectorAll(service.selector);
            iframes.forEach((iframe) => {
                if (iframeStateRef.current[service.name]) {
                    const iframeElement = iframe as HTMLIFrameElement;

                    // Set the iframe src and make it visible
                    setIframeSrc(iframeElement, "Resize handler");
                } else {
                    // Check if consent is given in the raw service object
                    const rawService = getUsercentricsServices()[service.serviceId];
                    const hasConsentInRawService = rawService?.consent?.given === true;

                    if (hasConsentInRawService) {
                        overlayStateRef.current[service.name] = false;
                        iframeStateRef.current[service.name] = true;

                        const iframeElement = iframe as HTMLIFrameElement;

                        // Set the iframe src and make it visible
                        setIframeSrc(iframeElement, "Resize handler consent");
                    } else if (overlayStateRef.current[service.name] !== false) {
                        blockOverlayForService(service);
                    }
                }
            });
        });
    }, 200);

    const handleInitialConsent = useCallback(
        (initialConsent: ConsentStatus) => {
            if (!isReady) {
                setPendingInitialConsent(initialConsent);
                return;
            }
            services.forEach(({ name }) => {
                handleOverlayService(name, initialConsent);
            });

            setTimeout(() => {
                debouncedResizeHandler();
            }, 100);
        },
        [isReady, services, handleOverlayService, debouncedResizeHandler]
    );

    useEffect(() => {
        fetchServices().catch((error) => {
            console.error(error)
        });
    }, []);

    useEffect(() => {
        if (isReady && pendingInitialConsent) {
            handleInitialConsent(pendingInitialConsent);
            setPendingInitialConsent(null);
        }
    }, [isReady, pendingInitialConsent, handleInitialConsent]);

    useEffect(() => {
        const handleConsentEvent = (e: CustomEvent<ConsentStatus & { action?: string }>) => {
            if (e.detail?.action === "onInitialPageLoad") {
                handleInitialConsent(e.detail);
            } else {
                handleConsentStatusChange(e.detail);
            }
        };

        window.addEventListener("consents_initialized", handleConsentEvent as EventListener);
        window.addEventListener("consent_changed", handleConsentEvent as EventListener);
        window.addEventListener("resize", debouncedResizeHandler);

        return () => {
            window.removeEventListener("consents_initialized", handleConsentEvent as EventListener);
            window.removeEventListener("consent_changed", handleConsentEvent as EventListener);
            window.removeEventListener("resize", debouncedResizeHandler);
        };
    }, [services, debouncedResizeHandler, handleConsentStatusChange, handleInitialConsent]);

    useEffect(() => {
        debouncedResizeHandler();
    }, [pathname, debouncedResizeHandler]);

    return {};
};

export default useOverlayService;
